/**
 * In-memory ring buffer of recent API calls — captured by the axios interceptor
 * in ./axios.js so the `ApiDevPanel` drawer can show every request/response
 * with full headers + body. Educational tool: lets demo viewers see the actual
 * UIC-shaped JSON the SPA exchanges with the backend without opening DevTools.
 *
 * The store is a module-level singleton; React components subscribe with
 * `subscribe(fn)` and call `getEntries()` from the callback.
 */

const MAX_ENTRIES = 100
const PAUSE_KEY = 'ngrs_api_panel_paused'

let entries = []
let nextId = 1
let paused = localStorage.getItem(PAUSE_KEY) === '1'
const listeners = new Set()

const notify = () => {
  // Snapshot the listener set so unsubscribe-during-notify is safe.
  for (const fn of [...listeners]) {
    try { fn(entries) } catch (e) { /* swallow listener errors */ }
  }
}

/** Strip the Authorization header from a header map for display — token is sensitive. */
function maskAuth(headers) {
  if (!headers) return {}
  const out = {}
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === 'authorization' && typeof v === 'string' && v.startsWith('Bearer ')) {
      out[k] = 'Bearer ••••••••' + v.slice(-6)
    } else {
      out[k] = v
    }
  }
  return out
}

/** Best-effort parse for stringified JSON request bodies (axios serializes them). */
function parseMaybeJson(body) {
  if (body == null) return null
  if (typeof body !== 'string') return body
  try { return JSON.parse(body) } catch { return body }
}

export function record(raw) {
  if (paused) return
  const entry = {
    id: nextId++,
    method: (raw.method || 'GET').toUpperCase(),
    url: raw.url || '',
    status: raw.status || 0,
    ok: !!raw.ok,
    duration: raw.duration || 0,
    timestamp: raw.timestamp || new Date().toISOString(),
    requestHeaders: maskAuth(raw.requestHeaders),
    requestBody: parseMaybeJson(raw.requestBody),
    responseHeaders: raw.responseHeaders || {},
    responseBody: raw.responseBody,
  }
  entries = [entry, ...entries].slice(0, MAX_ENTRIES)
  notify()
}

export function getEntries() {
  return entries
}

export function clear() {
  entries = []
  notify()
}

export function isPaused() {
  return paused
}

export function togglePaused() {
  paused = !paused
  localStorage.setItem(PAUSE_KEY, paused ? '1' : '0')
  notify()
  return paused
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * Build a copy-paste-able cURL command. Token redacted by default; if
 * `reveal=true`, embed the real token from localStorage so the demo viewer can
 * actually replay the request from a terminal.
 */
export function buildCurl(entry, { reveal = false } = {}) {
  const parts = ['curl', '-X', entry.method]
  const origin = window.location.origin
  parts.push(`'${origin}${entry.url}'`)

  for (const [k, v] of Object.entries(entry.requestHeaders || {})) {
    if (k.toLowerCase() === 'authorization' && reveal) {
      const token =
        localStorage.getItem('auth_token_admin') ||
        localStorage.getItem('auth_token_user') ||
        ''
      parts.push('-H', `'Authorization: Bearer ${token}'`)
    } else {
      parts.push('-H', `'${k}: ${v}'`)
    }
  }

  if (entry.requestBody != null) {
    const body =
      typeof entry.requestBody === 'string'
        ? entry.requestBody
        : JSON.stringify(entry.requestBody)
    // Escape single quotes for shell-safe inline body.
    parts.push('-d', `'${body.replace(/'/g, "'\\''")}'`)
  }

  return parts.join(' ')
}
